import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { fm } from 'Components/FormatMessage';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import Unit from 'Components/Unit';
import { styled } from '@mui/system';
import { resMedTreatmentResponseFieldToUnitMap } from '../../config/sleepApnea';
import { StyledTitle } from '..';
import { capitalize } from 'Utility/string';
import { getMinMaxFieldNames, getMinMaxFieldParent } from '../../functions/utils';

const InlineDiv = styled('div')({
  display: 'inline-flex',
});

const fields = ['averagePapDeviceUsage', 'averageResidueAhi', 'spikeResidueAhi', 'averageCai', 'averageMaskLeakage'];

const PapTreatmentResponseField = ({ searchFields, onChange }: IOwnProps): React.JSX.Element => {
  const thisSearchField =
    searchFields.papTreatmentResponse && searchFields.papTreatmentResponse !== 'noCropping'
      ? searchFields.papTreatmentResponse
      : undefined;

  const fieldToUnitMap = resMedTreatmentResponseFieldToUnitMap;

  const handleChange = (values: TOnChangeValues) => {
    let f = Object.keys(values)[0];
    let v = values[f];

    if (['rollingAverageWindow', 'rollingAverageThreshold'].includes(f)) {
      const parentValue =
        thisSearchField?.spikeResidueAhi && thisSearchField.spikeResidueAhi !== 'noCropping'
          ? thisSearchField.spikeResidueAhi
          : {};
      const newValue = { ...parentValue, [f]: v };
      if (Object.values(newValue).every(val => typeof val !== 'number')) {
        v = 'noCropping';
      } else {
        v = newValue;
      }
      f = 'spikeResidueAhi';
    }
    if (f.includes('min') || f.includes('max')) {
      const parentField = getMinMaxFieldParent(f);
      const fieldNames = getMinMaxFieldNames(parentField);
      const parentValue = (thisSearchField?.[parentField as keyof typeof thisSearchField] ?? {
        [fieldNames[0]]: undefined,
        [fieldNames[1]]: undefined,
      }) as { [key: string]: number | undefined };
      v = { ...parentValue, [f]: v };
      if (Object.values(v).every(val => typeof val !== 'number')) v = 'noCropping';
      f = parentField;
    }

    const baseValue = typeof thisSearchField === 'object' ? thisSearchField : {};
    const newValues: Partial<typeof thisSearchField> = { ...baseValue, [f]: v };

    onChange({ papTreatmentResponse: newValues });
  };

  return (
    <React.Fragment>
      <StyledTitle>{fm('extendedSearch.timeframe')}</StyledTitle>
      <InputHandler
        type="Radio"
        editing={true}
        name="timeframe"
        formData={{
          onChange: handleChange,
          document: { timeframe: thisSearchField?.timeframe },
        }}
        options={['last12months', 'last6months', 'last3months', 'last1months']}
        optionFormatter={(name: string | number): string | React.JSX.Element => fm(`extendedSearch.opts.${name}`)}
      />
      {thisSearchField?.timeframe &&
        fields.map((f, i) => {
          const v = thisSearchField[f as keyof typeof thisSearchField];
          switch (f) {
            case 'spikeResidueAhi': {
              return (
                <React.Fragment key={`${f}-${i}`}>
                  <StyledTitle>{fm(`extendedSearch.${f}`)}</StyledTitle>
                  <InlineDiv>
                    <Unit
                      unit={
                        <span>
                          {fm('extendedSearch.consecutiveNightsAverage')} {'>'}
                        </span>
                      }
                    >
                      <InputHandler
                        type="NumberField"
                        editing={true}
                        name="rollingAverageWindow"
                        formData={{
                          onChange: handleChange,
                          document: {
                            rollingAverageWindow:
                              typeof v === 'object' ? v['rollingAverageWindow' as keyof typeof v] : undefined,
                          },
                        }}
                        min={2}
                        max={30}
                        width={7}
                      />
                    </Unit>
                  </InlineDiv>
                  <InlineDiv>
                    <Unit unit={fm(`extendedSearch.${fieldToUnitMap.get(f)}`)}>
                      <InputHandler
                        type="NumberField"
                        editing={true}
                        name="rollingAverageThreshold"
                        formData={{
                          onChange: handleChange,
                          document: {
                            rollingAverageThreshold:
                              typeof v === 'object' ? v['rollingAverageThreshold' as keyof typeof v] : undefined,
                          },
                        }}
                        min={0}
                        width={7}
                        precision={2}
                      />
                    </Unit>
                  </InlineDiv>
                </React.Fragment>
              );
            }
            default:
              return (
                <React.Fragment key={`${f}-${i}`}>
                  <StyledTitle>{fm(`extendedSearch.${f}`)}</StyledTitle>
                  <Unit key={`${f}-${i}`} unit={fm(`extendedSearch.${fieldToUnitMap.get(f)}`)}>
                    <React.Fragment>
                      {[`min${capitalize(f)}`, `max${capitalize(f)}`].map((ff, ii) => {
                        const vv = v && typeof v === 'object' ? v[ff as keyof typeof v] : undefined;
                        return (
                          <React.Fragment key={`${ff}-${ii}`}>
                            <InputHandler
                              type="NumberField"
                              editing={true}
                              name={ff}
                              formData={{
                                onChange: handleChange,
                                document: { [ff]: vv === 'noCropping' ? undefined : vv },
                              }}
                              min={0}
                              placeholder={`extendedSearch.${{ 0: 'min', 1: 'max' }[ii]}`}
                              width={7}
                              precision={2}
                            />
                            {ii === 0 && ' - '}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  </Unit>
                </React.Fragment>
              );
          }
        })}
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'searchFields' | 'onChange'> {}

export default PapTreatmentResponseField;
